import React, {Suspense} from "react";
import PropTypes from "prop-types";
import { throttle } from "lodash";
import {Decimal} from 'decimal.js';

import { ToastContainer } from "react-toastify";
import { Zoom } from "@learningblocks/storybook-components/lib/ProtocolComponents/Zoom";
import { Undo } from "./Undo";
import { ProtocolScene } from "../ProtocolScene";
import { mapDataToTasks } from './TaskDataImporter'
import {MoveMenu} from "@learningblocks/storybook-components/lib/ProtocolComponents/MoveMenu"
import { calculatePosition, updateScale, teleport } from '../ProtocolView/hotspot'

import {
  start,
  drag,
  end,
  setDragImage,
  getTarget
} from "./DragFunctions"

import "./style.scss";

const DIMENSIONS = {
  width: 1515,
  height: 900,
};

export class ProtocolView extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      userScale: 1,
      sceneScale: 1,
      objCurrent: null,
      objPrevious: null
    };
  }

  componentWillMount(){

    this.props.setupProtocolScene({})

    const interactions = (heldObj, target) => {
      //Sets target of last drop for Pipette extraction/deposit
      this.props.setLastDrop({
        lastHeld: heldObj,
        lastTarget: target
      })
    }

    Object.defineProperty(interactions, 'name', {value: ('scene_interactions'), configurable: true});
    this.props.pushInteractions([interactions])

    // {level, data}
    this.props.setTaskPages(mapDataToTasks(1, this.props.sequence))
  }

  componentDidMount() {
    // Resize the scene and attach the resize method to the window resize event
    window.addEventListener("resize", this.handleResize)
    this.handleResize()

    var temp = document.getElementsByClassName("dropTarget")
    this.props.setTargetList(temp)
  }

  componentDidUpdate(prevProps, prevState){
    if(
      typeof prevProps.heldObject !== 'undefined' &&
      this.props.heldObject.htmlObj == null &&
      prevProps.heldObject.type != null
    ){
      this.runInteractions(prevProps.heldObject)
    }

    if(prevProps.notebookSection !== this.props.notebookSection && this.props.notebookSection === "Protocol"){
      this.handleResize()
    }
    
    if(
      this.state.userScale !== prevState.userScale
    ){
      this.handleResize()
    }

    if(this.props.scale !== prevProps.scale){
      this.handleResize()
    }

    if(this.props.notebookExpanded !== prevProps.notebookExpanded){
       this.handleResize()
    }

  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.handleResize);
  }

  getObjectFromId = (id) => {
    if (id === undefined || id === null) {
      return null
    }
    const idArr = id.split("_")
    let obj = {
      type: "",
      id: -1,
      subId: -1,
      subRef: -1,
      solution: "",
      tipType: ""
    }
    switch (idArr[0]) {
      case "P20":
      case "P100":
      case "P200":
      case "P1000":
        obj.type = "Pipette"
        obj.id = idArr[1]
        break;
      case "Solution":
        obj.type = "Solution"
        obj.id = idArr[1]
        obj.solution = this.props.solutions[obj.id - 1].type
        break;
      case "BlottingPaper":
        obj.type = "BlottingPaper"
        obj.id = idArr[1]
        obj.subId = idArr[2]
        break;
      case "Trash":
        obj.type = "Trash"
        obj.id = idArr[1]
        break;
      case "PipetteRack":
        obj.type = "PipetteRack"
        break;
      case "TubeStand":
        obj.type = "TubeStand"
        break;
      case "AgaroseGel":
        obj.type = "AgaroseGel"
        obj.id = idArr[1]
        break;
      case "GelBox":
        obj.type = "GelBox"
        obj.id = idArr[1]
        obj.subId = idArr[2]
        break;
      case "PowerSupplyLead":
        obj.type = "PowerSupplyLead"
        obj.id = idArr[1]
        obj.subId = idArr[2]
        obj.subRef = parseInt(idArr[4], 10)
        break;
      case "PowerSupply":
        obj.type = "PowerSupply"
        obj.id = idArr[1]
        obj.subId = idArr[2]
        obj.subRef = parseInt(idArr[3], 10)
        break;
      case "Flask":
        obj.type = "Flask"
        obj.id = idArr[1]
        break;
      case "Transilluminator":
        obj.type = "Transilluminator"
        obj.id = idArr[1]
        break;
      case "IceBucket":
        obj.type = "IceBucket"
        obj.id = idArr[1]
        break;
      case "Thermocycler":
        obj.type = "Thermocycler"
        obj.id = idArr[1]
        break;
      case "HeatBlock":
        obj.type = "HeatBlock"
        obj.id = idArr[1]
        break;
      case "FloatingTubeRack":
        obj.type = "FloatingTubeRack"
        obj.id = idArr[1]
        break;
      case "WaterBath":
        obj.type = "WaterBath"
        obj.id = idArr[1]
        break;
      case "Microcentrifuge":
        obj.type = "Microcentrifuge"
        obj.id = idArr[1]
        break;
      case "Cuvette":
        obj.type = "Cuvette"
        obj.id = idArr[1]
        break;
      case "CuvetteRack":
        obj.type = "CuvetteRack"
        obj.id = idArr[1]
        break;
      case "ExperimentPoster":
        obj.type = "ExperimentPoster"
        obj.id = idArr[1]
        break;
      case "LidShelf":
        obj.type = "LidShelf"
        obj.id = idArr[1]
        break;
      case "Light":
        obj.type = "Light"
        obj.id = idArr[1]
        break;
      case "Box":
        obj.type = "Box"
        obj.id = idArr[1]
        break;
      case "Timer":
        obj.type = "Timer"
        obj.id = idArr[1]
        break;
      case "Plant":
        obj.type = "Plant"
        obj.id = idArr[1]
        obj.subId = idArr[2]
        break;
      case "Stopper":
        obj.type = "Stopper"
        obj.id = idArr[1]
        break;
      case "Spectrophotometer":
        obj.type = "Spectrophotometer"
        obj.id = idArr[1]
        break;
      case "GlassTube":
        obj.type = "GlassTube"
        obj.id = idArr[1]
        break;
      case "CuvetteSeal":
        obj.type = "CuvetteSeal"
        obj.id = idArr[1]
        break;
      default:
        break;
    }
    if (idArr[1] === "TipBox") {
      obj.type = "TipBox"
      obj.id = idArr[2]
      obj.tipType = idArr[0]
    }
    obj.id = parseInt(obj.id)
    return obj
  }

  //Runs all interaction functions that have been added to the global array
  runInteractions = (heldObj) => {
    const target = getTarget(heldObj.htmlObj, this.props.targetList, this.state.userScale, this.state.sceneScale).id

    //if(target){
      this.props.interactions.forEach(interaction => {
        interaction(heldObj, this.getObjectFromId(target))
      });
    //}

    //interaction function structure
    //always takes 'target' arg -> what the component will be interacting with
    //function must internally get access to all states it needs
  }

  wrapperClass = () => {
    const classList = [
      "protocol-view",
      this.props.drawerOpen ? "drawer-active" : "",
    ];
    return classList.join(" ");
  };

  getScrollbarHeight = () => {
    return window.innerHeight - document.getElementById("scene").offsetHeight;
  }

  handleResize = () => {
    this.setState({
      sceneScale:
        (new Decimal(window.innerHeight).minus(this.getScrollbarHeight())).dividedBy(DIMENSIONS.height).times(this.state.userScale).toNumber()
    }, () => updateScale(new Decimal(this.state.sceneScale).times(this.state.userScale).toNumber()));
  };

  onDragStart = (e) => {

    // //Delete when working
    // var temp = document.getElementsByClassName("dropTarget")
    // this.props.setTargetList(temp)
    //

    this.setState({
      objCurrent: e.target
    })

    setDragImage(e)
    start(e, e.target, this.state.sceneScale, this.state.userScale)

  }

  onDragOver = throttle((e) => {
     e.preventDefault();
     drag(e, this.state.objCurrent, this.state.userScale, this.state.sceneScale);
   }, 10);


  //Check heldObj to {obj: pipette, id: ID}
  //Set held to false in target pipette
  //Set heldObj to null
  onDrop = (e) => {
    e.preventDefault()

    //Clear held object
    this.props.setHeldObject({
      type: null,
      id: -1,
      htmlObj: null,
    })

    end(e, this.state.objCurrent, this.state.objPrevious)

    if(this.state.objCurrent !== null){
      this.setState({
        objPrevious: this.state.objCurrent
      })
    }

    this.setState({
      objCurrent: null
    })
  }

  toggleMoveMenu = e => {
    this.props.showMoveMenu({ display: !this.props.moveMenuDisplay });
  };

  render() {
    return (
      <div className={this.wrapperClass()}>
        <div className="protocol-view__left-box">
          {/*<h1 className='protocol-view__title'>{this.props.title}</h1>*/}
          {/*<p className='protocol-view__description'>{this.props.description}</p>*/}
          <div style={{
            overflow: "auto",
            width: "100%",
            height: "100%",
            position: "absolute"
          }}>
            <div
              id="scene"
              ariaLabel={'A lab bench with a shelf above it. On the shelf, from left to right, is a stand containing tube stoppers and cuvette lids, a tank with water plants growing in it, and 2 boxes. A pH-scale poster is stuck on the wall. On the lab bench, from left to right, are 4 tubes filled with pH indicator solution (BTB), a stand with a P1000 micropipette, a pipette tip box labeled P1000 tips, a trash can, a stand containing 5 cuvettes, and a spectrophotometer. Above the tubes are 2 lightbulbs. The light switch is set to [off/on]. An experimental setup poster is stuck on the wall.'}
              style={{
                transform: "scale(" + this.state.userScale + ")",
                width: "100%",
                height: "100%",
                transformOrigin: "top left",
              }}
            >
              {/* ----- Drag and Drop Scene ------ */}
              <div
                id="dragdrop"
                style={{
                  //border: "5px solid red",
                  //boxSizing: "border-box",
                  width: DIMENSIONS.width + "px",
                  height: DIMENSIONS.height + "px",
                  transform: "scale(" + this.state.sceneScale + ")",
                  transformOrigin: "top left",
                  left: "40px"
                }}
                onDragStart={this.onDragStart}
                onDragOver={(e)=>{
                  e.persist();
                  this.onDragOver(e);
                }}
                onDragEnd={this.onDrop}
              >
                <ProtocolScene
                  apparatus={this.props.apparatus}
                  userScale={this.state.userScale}
                  sceneScale={this.state.sceneScale}
                />
              </div>
            </div>
          </div>
          {/* ----- UI Elements ------ */}
          <ToastContainer closeButton={false} role={"dialog"} />
          <Suspense fallback="loading">
            <MoveMenu
                component={this.props.moveMenuComponent}
                type={this.props.moveMenuType}
                connect={this.props.moveMenuConnect}
                modalOpen={this.props.moveMenuDisplay}
                targetList={this.props.targetList}
                toggleModal={() => this.toggleMoveMenu()}
                lang={this.props.lang}
                notebookExpanded={this.props.notebookExpanded}
                setHeld={this.props.setHeld}
                setHeldObject={this.props.setHeldObject}
                calculatePosition={calculatePosition}
                teleport={teleport}
              />
          </Suspense>
          <Zoom
            scale={this.state.userScale}
            setSceneScale={(scale) =>
              this.setState({
                userScale: scale.scale,
              })
            }
          />
          <Undo
            setModal={this.props.setModal}
            undoAttempts={this.props.undoAttempts}
            taskPage={this.props.taskPage}
            focusedTask={this.props.focusedTask}
          />
        </div>
      </div>
    );
  }
}

ProtocolView.propTypes = {
  drawerOpen: PropTypes.bool,
  title: PropTypes.string,
  sceneType: PropTypes.string,
  description: PropTypes.string,
};
